import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import moment from 'moment';
import { Helmet } from 'react-helmet';

import withWidth from '@material-ui/core/withWidth';

import { getFormValues } from 'redux-form';

import Progress from 'components/common/Progress';
import Layout from 'components/common/Layout';
import PatientsTabs from 'components/patient/PatientsTabs';
import PatientsFilter from 'components/patient/PatientsFilter';
import PatientsTable from 'components/patient/PatientsTable';

import { fetchPatients, clearPatients, cancelFetchPatients } from 'redux/modules/patients';
import { addRouterParamsPageSettings } from 'redux/modules/routerParams';
import { fetchBusiness } from 'redux/modules/business';

import { getFilterChangeTypes } from 'utils/filterChangeTypes';
import objectDiff from 'utils/objectDiff';
import throttle from 'utils/throttle';
import * as validate from 'utils/validate';

import dateFormat from 'config/dateFormat';

import { appTitle, titleSeparator } from 'config/helmetSettings';

class Charts extends Component {
  constructor(props) {
    super(props);

    this.state = {
      mobileFilterExpanded: ['xs', 'sm'].indexOf(props.width) === -1,
      limit: 10,
      offset: 0,
    };

    this.onRequest = this.onRequest.bind(this);
    this.onSortingChange = this.onSortingChange.bind(this);
    this.onMobileFilterBottonClick = this.onMobileFilterBottonClick.bind(this);
    this.onTableScroll = this.onTableScroll.bind(this);

    this.onThrottledRequest = throttle(this.onRequest, 2000, false, true);
  }

  componentWillMount() {
    this.props.actions.clearPatients();
  }

  componentDidMount() {
    this.onRequest();
    window.addEventListener('scroll', this.onWindowScroll);
  }

  componentWillReceiveProps(nextProps) {
    const { filters, width, sortBy, sort } = this.props;
    const { offset } = this.state;
    const filtersChanged =
      nextProps.filters['Name or PRN'] !== filters['Name or PRN']
      || nextProps.filters['Date of Birth'] !== filters['Date of Birth']
      || nextProps.filters['Insurance Code'] !== filters['Insurance Code']
      || nextProps.filters['Patient Status'] !== filters['Patient Status']
      || nextProps.filters['Watcher ID'] !== filters['Watcher ID'];
    const sortByChanged = nextProps.sortBy !== sortBy;
    const sortChanged = nextProps.sort !== sort;
    let newOffset = offset;
    if (filtersChanged || sortByChanged || sortChanged) {
      newOffset = 0;
    }

    if (newOffset !== offset) {
      this.setState({ offset: newOffset });
    }

    if (nextProps.width !== width) {
      this.setState({
        mobileFilterExpanded: ['xs', 'sm'].indexOf(nextProps.width) === -1,
      });
    }
  }

  shouldComponentUpdate(nextProps, nextState) {
    return ((Object.keys(objectDiff(this.props, nextProps)).length +
      Object.keys(objectDiff(this.state, nextState)).length) > 0);
  }

  componentDidUpdate(prevProps, prevState) {
    const { filters, sortBy, sort, patients, count, loading } = this.props;
    const { limit, offset } = this.state;
    const limitChanged = prevState.limit !== limit;
    const offsetChanged = prevState.offset !== offset;
    const { throttledFiltersChanged, filtersChanged } = getFilterChangeTypes(
      prevProps.filters,
      filters,
      {
        textFilterKeys: ['Name or PRN', 'Insurance Code'],
        immediateFilterKeys: ['Date of Birth', 'Patient Status', 'Watcher ID'],
      },
    );
    const sortByChanged = prevProps.sortBy !== sortBy;
    const sortChanged = prevProps.sort !== sort;
    if (throttledFiltersChanged) {
      this.onThrottledRequest();
    } else if (limitChanged || offsetChanged || filtersChanged || sortByChanged || sortChanged) {
      this.onRequest();
    } else if (patients.length < count
      && this.scrollRef.scrollTop === 0
      && loading === 0) {
      this.onTableScroll({ target: this.scrollRef });
    }
  }

  componentWillUnmount() {
    this.props.actions.cancelFetchPatients();
    this.props.actions.fetchBusiness({
      triggerLoading: false,
    });
  }

  onRequest(options = {}) {
    const { actions, filters, sortBy, sort } = this.props;
    const { limit, offset } = this.state;
    const requestFilters = filters && Object.keys(filters).length > 0
      ? Object.keys(filters).map((key) => {
        let name = key;
        let operation = 'startswith';
        let caseSensitivity = null;
        let val = filters[key];
        switch (key) {
          case 'Name or PRN':
            if (/^\d+$/.test(filters[key])) {
              name = 'Record Number';
              operation = 'eq';
            } else {
              name = 'Full Name';
              caseSensitivity = false;
            }
            break;
          case 'Date of Birth':
            operation = 'eq';
            break;
          case 'Patient Status':
            operation = 'eq';
            val = filters[key] === 'Inactive' ? false : true;
            break;
          case 'Insurance Code':
            operation = 'startswith';
            caseSensitivity = false;
            break;
          case 'Watcher ID':
            operation = 'in';
            val = [filters[key]];
            break;
          default:
            caseSensitivity = false;
        }
        return {
          name,
          operation,
          val,
          ...caseSensitivity !== null ? { case_sensitivity: caseSensitivity } : {},
        };
      })
      : null;
    actions.fetchPatients({
      limit,
      offset,
      ...requestFilters ? {
        filters: requestFilters,
      } : {},
      ...sortBy ? {
        sortBy: [{
          name: sortBy,
          sort,
        }],
      } : {},
      triggerLoading: true,
      push: options.push !== undefined ? options.push : offset > 0,
    });
  }

  onSortingChange(newSortBy) {
    const { actions, location, sort, sortBy } = this.props;
    let newSort;
    if (sortBy !== newSortBy) {
      newSort = 'asc';
    } else {
      newSort = sort === 'asc' ? 'desc' : 'asc';
    }
    actions.addRouterParamsPageSettings({
      [location.pathname]: {
        sortBy: newSortBy,
        sort: newSort,
      },
    });
  }

  onMobileFilterBottonClick() {
    this.setState({ mobileFilterExpanded: !this.state.mobileFilterExpanded });
  }

  onTableScroll(event) {
    const { offset, limit } = this.state;

    if ((event.target.offsetHeight + event.target.scrollTop) / event.target.scrollHeight > 0.9) {
      if ((this.props.patients.length - limit) >= offset) {
        this.setState({
          offset: offset + limit,
        });
      }
    }
  }

  render() {
    const { patients, count, loading, limit, offset, sortBy, sort, business, filters } = this.props;
    const { mobileFilterExpanded } = this.state;
    return (
      <Layout>
        <Helmet>
          <title>
            {appTitle} {titleSeparator} Charts
          </title>
        </Helmet>
        <PatientsTabs variant="charts" />
        <PatientsFilter
          form="chartsFilter"
          buttonHref="/charts/create/"
          onMobileFilterBottonClick={this.onMobileFilterBottonClick}
          mobileFilterExpanded={mobileFilterExpanded}
          filters={filters}
          loading={loading}
          count={count}
          items={patients}
        />
        {loading > 0 &&
          <Progress fixed />
        }
        <PatientsTable
          items={patients}
          page={offset / limit}
          rowsPerPage={limit}
          count={count}
          onChangePage={this.onChangePage}
          onChangeRowsPerPage={this.onChangeRowsPerPage}
          sort={sort}
          sortBy={sortBy}
          onSortingChange={this.onSortingChange}
          loading={loading}
          business={business}
          mobileFilterExpanded={mobileFilterExpanded}
          onScroll={this.onTableScroll}
          scrollRef={(scrollRef) => { this.scrollRef = scrollRef; }}
        />
      </Layout>
    );
  }
}

Charts.propTypes = {
  patients: PropTypes.arrayOf(PropTypes.shape({
    'First Name': PropTypes.string,
  })).isRequired,
  count: PropTypes.number.isRequired,
  loading: PropTypes.number.isRequired,
  filters: PropTypes.shape({
    'Name or PRN': PropTypes.string,
    'Date of Birth': PropTypes.string,
    'Insurance Code': PropTypes.string,
  }).isRequired,
  actions: PropTypes.shape({
    fetchPatients: PropTypes.func.isRequired,
    clearPatients: PropTypes.func.isRequired,
    addRouterParamsPageSettings: PropTypes.func.isRequired,
    fetchBusiness: PropTypes.func.isRequired,
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }).isRequired,
  limit: PropTypes.number,
  offset: PropTypes.number,
  sortBy: PropTypes.string,
  sort: PropTypes.oneOf(['asc', 'desc']),
  business: PropTypes.shape({
    ID: PropTypes.number,
  }),
};

Charts.defaultProps = {
  limit: 10,
  offset: 0,
  sortBy: 'First Name',
  sort: 'asc',
  business: {},
};

const mapStateToProps = (store, ownProps) => {
  const chartsFilterValues = getFormValues('chartsFilter')(store);
  return {
    patients: store.patients.data,
    count: store.patients.count,
    filters: {
      ...chartsFilterValues
        && chartsFilterValues['Name or PRN']
        ? { 'Name or PRN': chartsFilterValues['Name or PRN'] }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Date of Birth']
        && !validate.date(chartsFilterValues['Date of Birth'])
        && moment(chartsFilterValues['Date of Birth'], dateFormat).isValid()
        ? { 'Date of Birth': moment(chartsFilterValues['Date of Birth'], dateFormat).format(dateFormat) }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Insurance Code']
        ? { 'Insurance Code': chartsFilterValues['Insurance Code'] }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Patient Status']
        && chartsFilterValues['Patient Status'] !== 'All'
        ? { 'Patient Status': chartsFilterValues['Patient Status'] }
        : {},
      ...chartsFilterValues
        && chartsFilterValues['Watcher ID']
        ? { 'Watcher ID': chartsFilterValues['Watcher ID'] }
        : {},
    },
    business: store.business.data,
    loading: store.patients.loading + store.business.loading,
    ...store.routerParams.pageSettings[ownProps.location.pathname],
  };
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({
    fetchPatients,
    clearPatients,
    cancelFetchPatients,
    addRouterParamsPageSettings,
    fetchBusiness,
  }, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(withWidth()(Charts));



// WEBPACK FOOTER //
// ./src/pages/charts/Charts.js